import { Component, ViewEncapsulation, ElementRef, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { MatInput, MatInputModule } from '@angular/material/input';
import { Subscription, forkJoin, merge, of, map, filter, switchMap, debounceTime, distinctUntilChanged,
	firstValueFrom } from 'rxjs';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor, NgClass } from '@angular/common';

import { WizardStepperService } from '../../../services/wizard-stepper.service';
import { WizardStep } from '../../../models/app-wizard-state';
import { FormOffer, FormProduct } from '../../../models/form-offer';
import { PRODUCT_TYPE_F, PRODUCT_TYPE_FM } from '../../../models/offer';
import { AppWizardService } from '../../../services/app-wizard.service';
import { ProductService } from '../../../services/product.api.service';
import { WizardPageContent } from '../wizard-page-content';
import { UserService } from '../../../../shared/services/user.service';
import { AppOfferService } from '../../../services/app-offer.service';
import { TranslationService } from '../../../../shared/services/translation.service';
import {
	DialogQuestionUtils,
	QUESTION_RESET_REQUESTED_DELIVERY_TIME
} from '../../utils/dialog-question/dialog-question.component';
import { isoMonthParse,
	isoMonthToDate, isoWeekOrMonthToMoment, parseIsoWeekOrMonth } from '../../../models/iso-week-or-month-utils';
import { SoldToRelationshipGroup } from '../../../../shared/models/service';
import { DialogWarningComponent } from '../../../../shared/components/dialog-warning/dialog-warning.component';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { safe } from '../../../../shared/utils/safe';
import { CodeValue } from '../../../../shared/models/boc-available-value';
import { StepProdSpecComponent } from '../step-prodspec/step-prodspec.component';
import { PeriodComponent } from '../../utils/period/period.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { SortOptionsPipe } from '../../../../shared/pipes/sorting.pipe';

@Component({
	selector: 'copo-offer-wizard-stepheader',
	templateUrl: './step-header.component.html',
	styleUrl: './step-header.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		LoadingSpinnerComponent,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		MatIconModule,

		MatButtonModule,

		MatSelectModule,

		MatOptionModule,
		NgFor,

		MatTooltipModule,
		NgClass,
		PeriodComponent,
		StepProdSpecComponent,
		SortOptionsPipe
	]
})
export class StepHeaderComponent extends WizardPageContent implements OnDestroy, OnInit {
	@ViewChild('rfqRefField', { read: ElementRef }) rfqRefField!: ElementRef<MatInput>;
	@ViewChild('periodComponent', { read: ElementRef }) periodComponent!: ElementRef<HTMLElement>;

	@ViewChild('soldToField', { read: ElementRef }) soldToField!: ElementRef<MatOption>;

	@ViewChild('shipToField', { read: ElementRef }) shipToField!: ElementRef<MatOption>;

	@ViewChild('endUserField', { read: ElementRef }) endUserField!: ElementRef<MatOption>;

	@ViewChild('iddField', { read: ElementRef }) iddField!: ElementRef<MatOption>;

	@ViewChild('tmodeField', { read: ElementRef }) tmodeField!: ElementRef<MatOption>;

	@ViewChild('incotField', { read: ElementRef }) incotField!: ElementRef<MatOption>;

	@ViewChild('priceUnitField', { read: ElementRef }) priceUnitField!: ElementRef<MatOption>;

	@ViewChild('currencyField', { read: ElementRef }) currencyField!: ElementRef<MatOption>;

	iddList?: CodeValue[];
	tmodList?: CodeValue[];
	incotList?: CodeValue[];
	currencyList?: CodeValue[];
	unitList?: CodeValue[];
	userBocData?: SoldToRelationshipGroup[];
	soldToList?: string[];
	readonly form: FormOffer;
	wasChecked = false;
	isLoading = true;
	isSoldToAvailable = true;
	isTmode = true;

	bocs?: Map<string, BocClient>;
	soldToBocList?: BocClient[];
	incoTerms?: CodeValue[];
	tmodeSubscription?: Subscription;
	configForBoc = { codeProp: 'businessId', valueProp: 'name' };
	config = { codeProp: 'code', valueProp: 'description' };
	configForIncotList = { codeProp: 'description', valueProp: 'code' };
	confirmationPopupSub: Subscription | undefined;
	private sub?: Subscription;

	/**
	 * when period changed and there are selected products which were in previous period
	 */
	private readonly periodChangeSubscription: Subscription;

	private readonly VIEW_STEP_NUM = 0;
	private readonly TRUCK_LIST = ['CIP', 'DAP', 'DDP', 'FCA', 'DPU'];
	private readonly INLAND_WATERWAYS_LIST = ['CFR', 'CIF', 'CIP', 'FAS', 'FOB', 'DPU'];
	private readonly TRAIN_LIST = ['CIP', 'DAP', 'DDP', 'FCA', 'DPU'];
	private readonly MARITIME_LIST =
		['CFR', 'CIF', 'CIP', 'FAS', 'FCA', 'FOB', 'DPU', '26', '33', '24', '27', '22', '28', '25', '23'];

	constructor(
		private readonly appOfferService: AppOfferService,
		private readonly appWizardService: AppWizardService,
		private readonly productService: ProductService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,

		private readonly dialog: MatDialog,
		private readonly stepperService: WizardStepperService,

		private readonly snackBar: MatSnackBar
	) {
		super(WizardStep.HEADER, appWizardService.getState());
		this.form = this.appWizardService.getStateOfferForm();
		this.periodChangeSubscription = this.form.getFormPeriod().valueChanges
			.pipe(
				map(() => {
					const formProducts = this.form.getFormProducts();
					if (formProducts.length === 0) {
						return {};
					}
					const formPeriod = this.form.getFormPeriod();
					const newStart = formPeriod.getPeriodStartMonth();
					const newEnd = formPeriod.getPeriodEndMonth();
					if (newStart === undefined || newEnd === undefined) {
						return {};
					}
					const lastStart = formPeriod.getAndUpdateLastPeriodStartMonth(newStart);
					const lastEnd = formPeriod.getAndUpdateLastPeriodEndMonth(newEnd);
					const products: FormProduct[] = [];
					for (const formProduct of formProducts.getFormProducts()) {
						const rdt = <string | undefined> formProduct.getFormRdd().value;
						if (rdt && !this.isRdtInPeriod(rdt, newStart, newEnd)) {
							products.push(formProduct);
						}
					}
					if (products.length === 0) {
						return {};
					}
					return {
						start: lastStart,
						end: lastEnd,
						products
					};
				}),
				filter(lastPeriodAndProducts =>
					!(lastPeriodAndProducts.start === undefined && lastPeriodAndProducts.end === undefined))
			).subscribe(
				({ start, end, products }) => {
					new DialogQuestionUtils(this.dialog).open({
						data: QUESTION_RESET_REQUESTED_DELIVERY_TIME,
						opContinue: () => {
							if (!products) {
								return;
							}
							for (const productForm of products) {
								productForm.getFormRdd().setValue(undefined);
							}
						},
						opCancel: () => {
							const formPeriod = this.form.getFormPeriod();
							formPeriod.getAndUpdateLastPeriodStartMonth(start);
							formPeriod.getFormPeriodStartMonth().setValue(start, { emitEvent: false });
							formPeriod.getAndUpdateLastPeriodEndMonth(end);
							formPeriod.getFormPeriodEndMonth().setValue(end, { emitEvent: false });
						}
					});
				}
			);
		this.loadParameters();
		this.tmodList = this.bocAvailableValues('TMODE');
		this.iddList = this.bocAvailableValues('IDD');
		this.currencyList = this.bocAvailableValues('CURRENCY');
		this.unitList = this.bocAvailableValues('PRICINGUNIT');
		this.incotList = this.bocAvailableValues('INCOT');
		if (this.form.getFormTmode().value === null) {
			this.isTmode = false;
		}
		if (this.tmodList) {
			for (const x of this.tmodList) {
				if (x.code === <string> this.form.getFormTmode().value) {
					this.incoTerms = this.getIncotBasedOnTmode(<string> this.form.getFormTmode().value);
				}
			}
		}
	}

	get isError() {
		return this.appWizardService.getState().errorInPage;
	}

	ngOnInit() {
		this.stepperService.onStepChanged.emit({ step: this.VIEW_STEP_NUM });
		const today = new Date();
		const prevStartMonth = this.form.getFormPeriod().getPeriodStartMonth();
		if (prevStartMonth) {
			const prevStartDate = isoMonthToDate(isoMonthParse(prevStartMonth));
			if (today.getTime() > prevStartDate.getTime()) {
				this.form.getFormPeriod().setPeriod(undefined, undefined);
				this.showDateError(this.translationService.getTR('eoffer.wizard.period.error'));
			}
		}
		this.tmodeSubscription = merge(
			this.form.getFormTmode().valueChanges
		).pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(
			() => {
				this.isTmode = true;
				this.incoTerms = this.getIncotBasedOnTmode(<string> this.form.getFormTmode().value);
			}
		);
	}

	ngOnDestroy() {
		this.periodChangeSubscription.unsubscribe();
		if (this.sub) {
			this.sub.unsubscribe();
		}
		if (this.confirmationPopupSub) {
			this.confirmationPopupSub.unsubscribe();
		}
	}

	checkError() {
		this.wasChecked = true;
		this.form.getFormRfqRef().markAsTouched();
		this.form.getFormSoldTo().markAsTouched();
		this.form.getFormShipTo().markAsTouched();
		this.form.getFormEndUser().markAsTouched();
		this.form.getFormPeriod().getFormPeriodStartMonth().markAsTouched();
		this.form.getFormTmode().markAsTouched();
		this.form.getFormIdd().markAsTouched();
		this.form.getFormIncoterm().markAsTouched();
		this.form.getFormPricingUnit().markAsTouched();
		this.form.getFormCurrency().markAsTouched();
		if (!this.form.getFormRfqRef().valid) {
			this.rfqRefField.nativeElement.focus();
			return true;
		}
		if (!this.form.getFormSoldTo().valid) {
			this.soldToField.nativeElement.focus();
			return true;
		}
		if (!this.form.getFormShipTo().valid) {
			this.shipToField.nativeElement.focus();
			return true;
		}
		if (!this.form.getFormEndUser().valid) {
			this.endUserField.nativeElement.focus();
			return true;
		}

		if (this.form.getFormProducts().length === 0) {
			return true;
		}

		const today = new Date();
		const startMonth = this.form.getFormPeriod().getPeriodStartMonth();
		if (startMonth) {
			const startDate = isoMonthToDate(isoMonthParse(startMonth));
			if (today.getTime() >= startDate.getTime()) {
				this.form.getFormPeriod().setPeriod(undefined, undefined);
				this.showDateError(this.translationService.getTR('eoffer.wizard.period.selection.error'));
				return true;
			}
		}

		if (startMonth) {
			const startDate = isoMonthToDate(isoMonthParse(startMonth));
			let months;
			months = (startDate.getFullYear() - today.getFullYear()) * 12;
			months -= today.getMonth();
			months += startDate.getMonth();
			if (months >= 24) {
				this.form.getFormPeriod().setPeriod(undefined, undefined);
				this.showDateError(this.translationService.getTR('eoffer.wizard.period.selection.error'));
				return true;
			}
		}

		if (!this.form.getFormPeriod().getFormPeriodStartMonth().valid) {
			this.periodComponent.nativeElement.scrollIntoView();
			return true;
		}
		if (!this.form.getFormPeriod().getFormPeriodEndMonth().valid) {
			this.periodComponent.nativeElement.scrollIntoView();
			return true;
		}
		if (!this.form.getFormIdd().valid) {
			this.iddField.nativeElement.focus();
			return true;
		}
		if (!this.form.getFormTmode().valid) {
			this.tmodeField.nativeElement.focus();
			return true;
		}
		if (!this.form.getFormIncoterm().valid) {
			this.incotField.nativeElement.focus();
			return true;
		}
		if (!this.form.getFormPricingUnit().valid) {
			this.priceUnitField.nativeElement.focus();
			return true;
		}
		if (!this.form.getFormCurrency().valid) {
			this.currencyField.nativeElement.focus();
			return true;
		}
		return false;
	}

	showDateError(msg?: string) {
		this.snackBar.open(
			msg === undefined
				? this.translationService.getTR('quapp.error')
				: msg,
			undefined,
			{ duration: 3000 }
		);
	}

	checkErrorForDraft() {
		this.form.getFormRfqRef().markAsTouched();
		if (!this.form.getFormRfqRef().valid) {
			this.rfqRefField.nativeElement.focus();
			return true;
		}
		if (this.form.getFormRfqRef().valid) {
			const rfqField = this.form.getFormRfqRef();
			if (String(rfqField.value).trim() === '') {
				this.form.getFormRfqRef().setValue('');
				this.rfqRefField.nativeElement.focus();
				return true;
			}
		}

		return false;
	}

	changeSoldToValues() {
		if (this.form.getFormProducts().length > 0) {
			const newBoc = {
				soldTo: <string | undefined> this.form.getFormSoldTo().value,
				shipTo: <string | undefined> this.form.getFormShipTo().value,
				endUser: <string | undefined> this.form.getFormEndUser().value
			};
			const lastBoc = this.form.getAndUpdateLastBoc(newBoc);
			const dialogRef = this.dialog.open(DialogWarningComponent, {
				data: { iconName: 'alert',
					title: 'global.warning',
					contents: [
						this.translationService.getTR('eorder.customer.change.warning'),
						this.translationService.getTR('eorder.add.item.confirm.change')
					],
					cancelButtonEnabled: true }
			});
			firstValueFrom(dialogRef.afterClosed())
				.then(result => {
					if (result) {
						this.form.getFormProducts().clear();
						this.soldToChangeEvent();
					} else {
						this.form.getAndUpdateLastBoc(lastBoc);
						this.form.getFormSoldTo().setValue(lastBoc.soldTo, { emitEvent: false });
						this.openSnackBar(this.translationService.getTR('global.action.cancelled'));
					}
				})
				.catch((error: unknown) => {
					console.error('Unexpected Error', error);
					this.form.getAndUpdateLastBoc(lastBoc);
					this.form.getFormSoldTo().setValue(lastBoc.soldTo, { emitEvent: false });
					this.openSnackBar(this.translationService.getTR('global.error.unexpected'));
				});
		} else {
			this.soldToChangeEvent();
		}
	}

	soldToChangeEvent() {
		this.productService.emitSoldToChangeEvent(<string> this.form.getFormSoldTo().value);
		const shipToList = this.getShipToList();
		if (!shipToList.some(shipto => shipto.businessId === this.form.getFormShipTo().value)) {
			this.form.getFormShipTo().setValue(undefined);
		}
		const endUserList = this.getEndUserList();
		if (!endUserList.some(enduser => enduser.businessId === this.form.getFormEndUser().value)) {
			this.form.getFormEndUser().setValue(undefined);
		}
		this.changeBocValues();
	}

	changeBocValues() {
		const newBoc = {
			soldTo: <string | undefined> this.form.getFormSoldTo().value,
			shipTo: <string | undefined> this.form.getFormShipTo().value,
			endUser: <string | undefined> this.form.getFormEndUser().value
		};
		const lastBoc = this.form.getAndUpdateLastBoc(newBoc);
		const formProducts = this.form.getFormProducts();
		if (formProducts.length > 0) {
			const indexListToRemove: number[] = [];
			let index = 0;
			let shipToMisMatch = false;
			for (let i = 0; i < formProducts.length; i++) {
				const formProduct = formProducts.getFormProductAt(i);
				const productShipTo = String(formProduct.getFormShipToParty().value).replace(/^0+/, '');
				const productEndUser = String(formProduct.getFormEndUser().value).replace(/^0+/, '');
				if (formProduct.get('productType')?.value === PRODUCT_TYPE_F) {
					if (newBoc.shipTo && newBoc.shipTo !== productShipTo) {
						shipToMisMatch = true;
						indexListToRemove.push(index);
					} else if (newBoc.endUser && newBoc.endUser !== productEndUser) {
						indexListToRemove.push(index);
					}
				} else if (formProduct.get('productType')?.value === PRODUCT_TYPE_FM) {
					const templateAndDetail = this.appWizardService.getState().ui.getTemplateNumberDetails(
						Number(formProduct.get('templateNumber')?.value)
					);
					if (templateAndDetail) {
						const variableNames = Object.getOwnPropertyNames(templateAndDetail.properties);
						if (newBoc.shipTo && newBoc.shipTo !== productShipTo) {
							if (variableNames.includes('shipToParty')) {
								formProduct.getFormShipToParty().setValue(newBoc.shipTo);
								this.resetSimulationStatus(formProduct);
							} else {
								shipToMisMatch = true;
								indexListToRemove.push(index);
							}
						}
						if (newBoc.endUser && newBoc.endUser !== productEndUser) {
							if (variableNames.includes('endUser')) {
								formProduct.getFormEndUser().setValue(newBoc.endUser);
								this.resetSimulationStatus(formProduct);
							} else if (!indexListToRemove.includes(index)) {
								indexListToRemove.push(index);
							}
						}
					}
				}
				index++;
			}
			if (indexListToRemove.length > 0) {
				const dialogRef = this.dialog.open(DialogWarningComponent, {
					data: {
						iconName: 'alert',
						title: 'global.warning',
						contents: [
							this.translationService.getTR(
								shipToMisMatch ? 'offer.shipto.update.cart.warning'
									: 'offer.enduser.update.cart.warning'
							)
						],
						cancelButtonEnabled: true
					}
				});
				this.confirmationPopupSub = dialogRef.afterClosed()
					.subscribe(result => {
						if (result) {
							const sortedIndexListToRemove = indexListToRemove.toSorted((a, b) => b - a);
							for (const indexToRemove of sortedIndexListToRemove) {
								this.form.getFormProducts().removeAt(indexToRemove);
							}
							this.productService.emitShipToEndUserChangeEvent(newBoc);
						} else {
							this.form.getAndUpdateLastBoc(lastBoc);
							this.form.getFormShipTo().setValue(lastBoc.shipTo, { emitEvent: false });
							this.form.getFormEndUser().setValue(lastBoc.endUser, { emitEvent: false });
							this.openSnackBar(this.translationService.getTR('global.action.cancelled'));
						}
					});
			} else {
				this.productService.emitShipToEndUserChangeEvent(newBoc);
			}
		} else {
			this.productService.emitShipToEndUserChangeEvent(newBoc);
		}
	}

	getShipToList() {
		const soldToBusinessId = <string> this.form.getFormSoldTo().value;
		const soldToGroup = soldToBusinessId && this.userBocData
			? this.userBocData.find(v => v.soldTo === soldToBusinessId)
			: undefined;
		const { bocs } = this;
		const shipToList = soldToGroup && bocs
			? soldToGroup.shipTos.map(v => bocs.get(v)).filter((v): v is BocClient => v !== undefined)
			: [];
		this.appWizardService.shipToList = shipToList;
		return shipToList;
	}

	getEndUserList() {
		const soldToBusinessId = <string> this.form.getFormSoldTo().value;
		const soldToGroup = soldToBusinessId && this.userBocData
			? this.userBocData.find(v => v.soldTo === soldToBusinessId)
			: undefined;
		const { bocs } = this;
		const endUserList = soldToGroup && bocs
			? soldToGroup.endUsers.map(v => bocs.get(v)).filter((v): v is BocClient => v !== undefined)
			: [];
		this.appWizardService.endUserList = endUserList;
		return endUserList;
	}

	getCustomerInfo() {
		return this.form.getFormSoldTo().valid;
	}

	private resetSimulationStatus(formProduct: FormProduct) {
		formProduct.getSimulationCompleted().setValue('');
		formProduct.getConfigErrMsg().setValue('');
		formProduct.product.isSimulationCompleted = '';
		formProduct.product.configErrMsg = '';
	}

	private loadParameters() {
		this.sub = forkJoin([
			this.appOfferService.getBocs()
				.pipe(switchMap(userBocData => {
					if (userBocData.length === 0) {
						return of(<const> [userBocData, <BocClient[]> []]);
					}
					const businessIds = Array.from(
						new Set(userBocData.flatMap(v => [v.soldTo, ...v.shipTos, ...v.endUsers]))
					);
					return forkJoin([
						of(userBocData),
						this.userService.getBocs(
							['businessId', 'name', 'city', 'address', 'country', 'postalCode'],
							businessIds.map(x => `businessId:${ x }`).join(' OR ')
						).pipe(map(v => v.results))
					]);
				}))
		]).subscribe({
			next: ([[userBocData, bocs]]) => {
				this.userBocData = userBocData;
				this.isLoading = false;
				for (const boc of bocs) {
					const { country } = boc;
					const lang = this.translationService.getLangWithRegion();
					const regionNames = new Intl.DisplayNames([lang], { type: 'region' });
					const countryName = (country ? regionNames.of(country) : undefined) || undefined;
					if (countryName) {
						boc.country = countryName;
					}
				}
				const bocsMap = this.bocs = new Map(bocs.map(v => [v.businessId, v]));
				const errorMsg = this.checkSoldToShipToEndUserError();
				if (errorMsg !== undefined) {
					this.appWizardService.getState().errorInPage = true;
					this.appOfferService.showError(errorMsg);
					return;
				}
				this.soldToList = this.userBocData.map(v => v.soldTo);
				this.soldToBocList = this.soldToList.map(v => safe(bocsMap.get(v)));
				this.appWizardService.soldToList = this.soldToBocList;
				this.setupDefaultHeader();
				this.setupDefaultSoldTo(this.soldToBocList);
				if (this.form.getFormSoldTo().value && this.form.getFormShipTo().value
					&& this.form.getFormEndUser().value) {
					this.appWizardService.isDefaultFamily = true;
				}
				if (this.form.getFormSoldTo().value) {
					this.productService.emitSoldToChangeEvent(<string> this.form.getFormSoldTo().value);
				}
			},
			error: (err: unknown) => {
				console.error('error loading wizard parameters', err);
				this.isLoading = false;
				this.isSoldToAvailable = false;
				this.appWizardService.getState().errorInPage = true;
				this.appOfferService.showError(
					this.translationService.getTR('eoffer.wizard.parameters.error')
				);
			},
		});
	}

	private setupDefaultSoldTo(soldToBocList: BocClient[]) {
		if (soldToBocList.length === 1 && (this.form.getFormSoldTo().value === null
			|| !this.form.getFormSoldTo().value)) {
			for (const soldTo of soldToBocList) {
				this.form.getFormSoldTo().setValue(soldTo.businessId);
			}
		}

		if (soldToBocList.length === 0) {
			this.isSoldToAvailable = false;
		}
	}

	private setupDefaultHeader() {
		const settingOfferHeader = this.userService.user.getSettings('offer_header');
		const soldToSetting = settingOfferHeader ? settingOfferHeader.value.offerHeader.soldTo : undefined;
		const shipToSetting = settingOfferHeader ? settingOfferHeader.value.offerHeader.shipTo : undefined;
		const endUserSetting = settingOfferHeader ? settingOfferHeader.value.offerHeader.endUser : undefined;

		const shipToList: string [] = this.userBocData ? this.userBocData.flatMap(v => v.shipTos) : [];
		const endUserList: string [] = this.userBocData ? this.userBocData.flatMap(v => v.endUsers) : [];

		if ((this.form.getFormSoldTo().value === null || !this.form.getFormSoldTo().value)
			&& (soldToSetting && this.soldToList?.includes(soldToSetting))) {
			this.form.getFormSoldTo().setValue(soldToSetting);
		}

		if ((this.form.getFormShipTo().value === null || !this.form.getFormShipTo().value)
			&& (shipToSetting && shipToList.length > 0 && shipToList.includes(shipToSetting))) {
			this.form.getFormShipTo().setValue(shipToSetting);
		}

		if ((this.form.getFormEndUser().value === null || !this.form.getFormEndUser().value)
			&& (endUserSetting && endUserList.length > 0 && endUserList.includes(endUserSetting))) {
			this.form.getFormEndUser().setValue(endUserSetting);
		}
		this.form.refreshLastBoc();

		if (this.form.getFormIdd().value === null || !this.form.getFormIdd().value) {
			this.form.getFormIdd().setValue(
				settingOfferHeader
					? settingOfferHeader.value.offerHeader.idd
					: undefined
			);
		}

		if (this.form.getFormTmode().value === null || !this.form.getFormTmode().value) {
			this.form.getFormTmode().setValue(
				settingOfferHeader
					? settingOfferHeader.value.offerHeader.tmode
					: undefined
			);
		}
		if (this.form.getFormIncoterm().value === null || !this.form.getFormIncoterm().value) {
			this.form.getFormIncoterm().setValue(
				settingOfferHeader
					? settingOfferHeader.value.offerHeader.incot
					: undefined
			);
		}

		if (this.form.getFormPricingUnit().value === null || !this.form.getFormPricingUnit().value) {
			this.form.getFormPricingUnit().setValue(
				settingOfferHeader
					? settingOfferHeader.value.offerHeader.pricingUnit
					: undefined
			);
		}
		if (!(this.form.getFormCurrency().value === null || !this.form.getFormCurrency().value)) {
			return;
		}
		this.form.getFormCurrency().setValue(
			settingOfferHeader
				? settingOfferHeader.value.offerHeader.currency
				: undefined
		);
	}

	private checkSoldToShipToEndUserError() {
		if (!this.userBocData || this.userBocData.length === 0) {
			return this.translationService.getTR('eoffer.error.nosoldto');
		}
		// search soldtos with at least one shipto and one enduser
		if (this.userBocData.filter(v => v.shipTos.length > 0 && v.endUsers.length > 0).length === 0) {
			for (const boc of this.userBocData) {
				const noShipto = boc.shipTos.length === 0;
				const noEndUser = boc.endUsers.length === 0;
				if (!noShipto && !noEndUser) {
					continue;
				}
				return noShipto && noEndUser
					? this.translationService.getTR('eoffer.error.noshiptoenduser', { value: boc.soldTo })
					: noShipto
						? this.translationService.getTR('eoffer.error.noshipto', { value: boc.soldTo })
						: this.translationService.getTR('eoffer.error.noenduser', { value: boc.soldTo });
			}
		}
		return undefined;
	}

	private isRdtInPeriod(rdt: string, start: string, end: string) {
		const mRdt = isoWeekOrMonthToMoment(parseIsoWeekOrMonth(rdt));
		const mStart = isoWeekOrMonthToMoment(parseIsoWeekOrMonth(start));
		const mEnd = isoWeekOrMonthToMoment(parseIsoWeekOrMonth(end));
		return mStart.isSameOrBefore(mRdt) && mRdt.isSameOrBefore(mEnd);
	}

	private bocAvailableValues(parameter: string) {
		return this.userService.getBocAvailableValuesBasedOnCode('EOFFR', parameter);
	}

	private getIncotBasedOnTmode(tmode: string) {
		let incoTermList2: string[] = [];
		switch (tmode) {
		case '3':
			incoTermList2 = this.TRUCK_LIST;
			break;
		case '5':
			incoTermList2 = this.INLAND_WATERWAYS_LIST;
			break;
		case '2':
			incoTermList2 = this.TRAIN_LIST;
			break;
		case '1':
			incoTermList2 = this.MARITIME_LIST;
			break;
		}

		let incotfinalList: CodeValue[] = [];
		if (this.incotList) {
			incotfinalList = this.incotList.filter(
				selectedBatch => incoTermList2.includes(
					selectedBatch.code
				)
			);
		}
		if (!incotfinalList.some(x => x.code === this.form.getFormIncoterm().value)) {
			this.form.getFormIncoterm().setValue(undefined);
		}

		return incotfinalList.length > 0 ? incotfinalList : [];
	}

	private openSnackBar(message: string) {
		this.snackBar.open(message, undefined, { duration: 5000 });
	}
}
